import type { ReactNode } from 'react';
import { View } from 'react-native';

import { Button } from '../primitives/Button';
import type { useTheme } from './theme';
import type { HistoryPanelStyles } from './HistoryPanel.styles';

// LG-59 — THE row of the history widget. One shape for all three sections
// (the computed weights, this exercise's history, a similar exercise's), so
// «Copia» always means the same thing wherever the athlete meets it.
//
// ⭐ Antenore, 2026-09-02, choosing the affordance: the computed values apply
// «ma sempre con il pulsante Copy». A tap on the row itself would have been
// one gesture with two meanings depending on which section it landed in.
//
// ⛔ The button is <Button compact={false}>, not the hand-rolled `copyBtn`
// this panel used to carry: box, padding, radius and pressed state are the
// primitive's job (§CONSOLIDAMENTO). And `secondary`, not `primary` — in a
// scrollable list of a dozen rows a filled brand box per row would make the
// loudest thing on the sheet the chrome, not the data.
//
// ⛔ hitSlop is horizontal-8 / vertical-4 against a row gap of s3 (8): the
// full 8 all round would have each button's tap area reach INTO the row above
// and below and silently steal their taps — the trap HeaderTooltip already
// paid for once. 40 + 4 + 4 = 48, so the floor is cleared without overlap.
export function HistoryCopyRow({
  children,
  onCopy,
  copyLabel,
  copyA11yLabel,
  styles,
  theme,
  highlighted = false,
}: {
  /** The row's content — caller-composed, because the three sections say
   *  different things about the same gesture. */
  children: ReactNode;
  /** Absent = no copy target (the exercise card, where no set is open):
   *  the row renders as pure information. */
  onCopy?: () => void;
  copyLabel: string;
  copyA11yLabel: string;
  styles: HistoryPanelStyles;
  theme: ReturnType<typeof useTheme>;
  /** The value in use, in the computed section. */
  highlighted?: boolean;
}) {
  return (
    <View style={[styles.copyRow, highlighted && styles.copyRowInUse]}>
      <View style={styles.copyRowMain}>{children}</View>
      {onCopy ? (
        <Button
          theme={theme}
          label={copyLabel}
          accessibilityLabel={copyA11yLabel}
          variant="secondary"
          onPress={onCopy}
          hitSlop={{ top: 4, bottom: 4, left: 8, right: 8 }}
        />
      ) : null}
    </View>
  );
}
